/**
 * Connection singleton — network-aware.
 *
 * Relocated from frontend/src/lib/tx-engine/connection.ts as part of the auth
 * boundary migration (spec 009 FILE 14 Group E). The frontend version read
 * `@/lib/network` directly; the SDK core MUST NOT import a frontend network
 * subsystem (.claude/rules/platform-agnostic.md). Instead the resolved
 * connection config (`TxConnectionConfig`) is passed in — the host resolves
 * the active-network RPC endpoint and threads it via `TxEngineConfig`.
 *
 * `getConnection(cfg)` rebuilds the Solana `Connection` whenever the
 * `network` in the supplied config changes. Platform-neutral: imports only
 * @solana/web3.js (universal).
 */

import { Connection } from '@solana/web3.js';

import type { TxConnectionConfig, TxEngineLogger } from '../config.js';
import type { WsHealthState } from '../types.js';

let connectionInstance: Connection | null = null;
let connectionNetwork: string | null = null;
let wsHealth: WsHealthState = 'disconnected';

/**
 * Derive a secure WebSocket endpoint from an HTTPS RPC URL.
 *
 * FQ-WSS-001 — only secure WebSocket endpoints are permitted in SDK `src/`.
 * Production and RN RPC endpoints are always `https://`, so only the secure
 * `wss://` swap is needed. For a non-https RPC URL the caller should pass an
 * explicit `wsUrl` in the connection config; absent that we return the URL
 * unchanged (the Solana `Connection` then derives its own ws endpoint).
 */
function deriveWsEndpoint(httpUrl: string): string {
  if (httpUrl.startsWith('https://')) {
    return `wss://${httpUrl.slice('https://'.length)}`;
  }
  return httpUrl;
}

/**
 * Get or create the singleton Connection for the supplied config.
 * Rebuilds when the active network has changed since the last call.
 */
export function getConnection(cfg: TxConnectionConfig): Connection {
  const network = cfg.network;
  if (connectionInstance !== null && connectionNetwork === network) {
    return connectionInstance;
  }

  // Network changed (or first call) — rebuild against the new RPC URL.
  const httpUrl = cfg.rpcUrl;
  const wsEndpoint = cfg.wsUrl ?? deriveWsEndpoint(httpUrl);

  // `instrumentFetch` is an optional host-supplied fetch override (web dev
  // tooling intercepts JSON-RPC for the DevToolkit Helius tab). Production
  // and RN omit it — the SDK never imports the web instrumentation.
  connectionInstance = new Connection(httpUrl, {
    wsEndpoint,
    commitment: 'confirmed',
    ...(cfg.instrumentFetch !== undefined ? { fetch: cfg.instrumentFetch } : {}),
  });
  connectionNetwork = network;
  wsHealth = 'connected';

  return connectionInstance;
}

/**
 * Pre-warm the connection by issuing a getSlot call.
 * Non-fatal: if it fails or times out, log a warning and proceed.
 */
export async function warmConnection(
  connection: Connection,
  logger: TxEngineLogger,
): Promise<void> {
  try {
    const timeout = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('warmConnection timeout')), 5000);
    });
    await Promise.race([connection.getSlot(), timeout]);
  } catch (e: unknown) {
    logger.warn('Connection warm-up failed (non-fatal)', {
      errorClass: e instanceof Error ? 'Error' : typeof e,
    });
  }
}

/** Clear the singleton for teardown. */
export function destroyConnection(): void {
  connectionInstance = null;
  connectionNetwork = null;
  wsHealth = 'disconnected';
}

/** Get the current WebSocket health state. */
export function getWsHealth(): WsHealthState {
  return wsHealth;
}

/** Update the WebSocket health state (used internally by confirmation module). */
export function setWsHealth(state: WsHealthState): void {
  wsHealth = state;
}
